import {
  COLOR_FLYWEIGHT,
  COLOR_SCALE_0,
  COLOR_SCALE_40,
  COLOR_SCALE_NEGATIVE_40,
  DEFAULT_CHART_COLORS,
  formatData,
} from '@utilities';

import { addSlideFooter } from '../slide_footer';
import { cleanQuestionTitle } from 'app/pages/Dashboard/pages/Survey/components/Dashboard/utilities/helpers.js';
import { FOOTER_TEXT, SURVEY_TITLE_OPTIONS, TABLE_BORDER, TEXT_COLOR } from '../constants';

/**
 * Calculates optimal distribution of items across slides
 * @param {number} totalItems - Total number of items to distribute
 * @param {number} maxItemsPerSlide - Maximum items per slide
 * @param {number} minItemsPerSlide - Minimum items per slide
 * @returns {Object} - { itemsPerSlide, totalSlides }
 */
const calculateOptimalSlideDistribution = (totalItems, maxItemsPerSlide, minItemsPerSlide) => {
  let itemsPerSlide = maxItemsPerSlide;
  let totalSlides = Math.ceil(totalItems / itemsPerSlide);

  // Find the most even distribution
  while (
    totalSlides > 1 &&
    totalItems % itemsPerSlide < minItemsPerSlide &&
    itemsPerSlide > minItemsPerSlide
  ) {
    itemsPerSlide--;
    totalSlides = Math.ceil(totalItems / itemsPerSlide);
  }

  return { itemsPerSlide, totalSlides };
};

/**
 * Gets chart and table data using MaxDiff component logic
 * This mirrors exactly what the MaxDiff React component does
 * @param {Object} data - Full MaxDiff data object
 * @param {String} displayAs - Display format
 * @param {String} segmentId - Optional segment ID, defaults to first segment
 * @returns {Object} - { chartData, tableData, isDisplayAsPercent }
 */
const getMaxDiffDisplayData = (data, displayAs = 'pct', segmentId = null) => {
  if (!data?.segments?.length) {
    return { chartData: null, isDisplayAsPercent: false, tableData: null };
  }

  const isCountDisplay = displayAs === 'count';
  const isDisplayAsPercent = !isCountDisplay;

  const selectedSegmentId = segmentId || data.segments[0]?.id;
  const selectedSegment = data.segments.find(({ id }) => id === selectedSegmentId);

  if (!selectedSegment) {
    return { chartData: null, isDisplayAsPercent, tableData: null };
  }

  const chartToDisplay = isCountDisplay ? selectedSegment?.chartCount : selectedSegment?.chartPct;
  const tableToDisplay = isCountDisplay ? selectedSegment?.tableCount : selectedSegment?.tablePct;

  return {
    chartData: chartToDisplay,
    isDisplayAsPercent,
    selectedSegment,
    tableData: tableToDisplay,
  };
};

/**
 * Creates a custom MaxDiff table slide using the same formatting as the MaxDiff component
 * @param {Object} options
 * @param {Object} options.pptx - PptxGenJS instance
 * @param {String} options.slideTitle - Title for the slide
 * @param {Object} options.tableData - Table data from MaxDiff component
 * @param {String} options.reportType - Report type
 * @param {String} options.reportUrl - Report URL
 * @param {String} options.title - Report title for footer
 * @returns {Object} slide
 */
const createSingleMaxDiffTableSlide = ({
  pptx,
  reportType,
  reportUrl,
  slideTitle,
  tableData,
  title,
}) => {
  const slide = pptx.addSlide();

  // Add title
  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(slideTitle, titleOptions);

  const tableRows = [];

  const headerRow = tableData.columns.map((col) => ({
    options: {
      bold: true,
      color: TEXT_COLOR,
      fill: { color: COLOR_FLYWEIGHT },
      fontSize: 11,
    },
    text: col.Header || col.accessor,
  }));
  tableRows.push(headerRow);

  tableData.data.forEach((row) => {
    const tableRow = tableData.columns.map((col) => {
      const value = row[col.accessor];
      let formattedValue;

      if (col.format && value !== null && value !== undefined) {
        formattedValue = formatData({ format: col.format, value });
      } else {
        // Handle cases where no format is specified
        if (typeof value === 'number') {
          // Default to count formatting for numbers without format
          formattedValue = Math.abs(value).toLocaleString();
        } else {
          formattedValue = value?.toString() || 'N/A';
        }
      }

      return {
        options: {
          align: col.accessor === 'option' ? 'left' : 'center',
          color: TEXT_COLOR,
          fontSize: 11,
          margin: [2, 2, 2, 2],
        },
        text: formattedValue,
      };
    });
    tableRows.push(tableRow);
  });

  // Calculate table dimensions
  const tableY = titleOptions.h > 1.0 ? 1.6 : 1.3;
  const tableHeight = 10 - tableY - 0.5;

  // Add table with compact styling
  slide.addTable(tableRows, {
    autoPage: false,
    autoPageHeaderRows: 1,
    autoPageRepeatHeader: true,
    border: TABLE_BORDER,
    color: TEXT_COLOR,
    fontSize: 11,
    h: tableHeight,
    margin: [1, 3, 1, 3],
    rowH: 0.3,
    w: 9,
    x: 0.5,
    y: tableY,
  });

  addSlideFooter({ pptx, reportType, reportUrl, slide });
  slide.addText(
    [
      { text: 'Source: ' },
      { options: { hyperlink: { url: reportUrl } }, text: title || 'MaxDiff Report' },
    ],
    FOOTER_TEXT
  );

  return slide;
};

/**
 * Converts MaxDiff chart data to PowerPoint format using the same logic as MaxDiff component
 * @param {Object} chartData - Chart data from MaxDiff component (chartPct or chartCount)
 * @param {String} displayAs - Display format
 * @param {Object} tableData - Optional table data to use for ordering
 * @returns {Object} - PowerPoint-formatted chart data
 */
const convertMaxDiffChartToPowerPoint = (chartData, displayAs, tableData = null) => {
  if (!chartData?.series || !chartData?.xAxis?.categories) {
    return null;
  }

  const isCountDisplay = displayAs === 'count';
  const isDisplayAsPercent = !isCountDisplay;
  const categories = chartData.xAxis.categories;

  const maxDiffColors = [COLOR_SCALE_40, COLOR_SCALE_NEGATIVE_40, COLOR_SCALE_0];

  let categoryIndices = categories.map((_, index) => index);

  if (tableData?.data && Array.isArray(tableData.data)) {
    const categoryOrderMap = {};
    tableData.data.forEach((row, rowIndex) => {
      const categoryIndex = categories.findIndex((cat) => cat === row.option);
      if (categoryIndex !== -1) {
        categoryOrderMap[categoryIndex] = row.rank || rowIndex;
      }
    });

    categoryIndices.sort((a, b) => {
      const orderA = categoryOrderMap[a] !== undefined ? categoryOrderMap[a] : 999;
      const orderB = categoryOrderMap[b] !== undefined ? categoryOrderMap[b] : 999;
      return orderA - orderB;
    });
  } else {
    const mostSeriesData = chartData.series?.[0]?.data || [];
    categoryIndices.sort((a, b) => {
      const valueA = parseFloat(mostSeriesData[a]) || 0;
      const valueB = parseFloat(mostSeriesData[b]) || 0;
      return valueB - valueA;
    });
  }

  const sortedCategories = categoryIndices.map((index) => categories[index]);

  const dataSeries = chartData.series.map((seriesItem, seriesIndex) => {
    const sortedSeriesData = categoryIndices.map((index) => {
      const value = seriesItem.data[index];
      if (value === null || value === undefined || isNaN(value)) {
        return 0;
      }
      const numericValue = parseFloat(value) || 0;

      // For percentage data, values should be between 0-1 (will be formatted as 0-100%)
      // For count data, use absolute values to handle negative counts properly
      if (isDisplayAsPercent) {
        // Convert percentage values to decimal if they're in 0-100 range
        return numericValue > 1 ? numericValue / 100 : numericValue;
      } else {
        // For count data, ensure we use absolute values
        return Math.abs(numericValue);
      }
    });

    return {
      color: maxDiffColors[seriesIndex] || DEFAULT_CHART_COLORS[seriesIndex],
      labels: sortedCategories,
      name: seriesItem.name || `Series ${seriesIndex + 1}`,
      values: sortedSeriesData,
    };
  });

  return {
    categoryLabels: sortedCategories,
    dataSeries: dataSeries,
    format: chartData.format,
    isPercentageDisplay: isDisplayAsPercent,
  };
};

/**
 * Creates MaxDiff chart slides using PptxGenJS addChart
 * @param {Object} options
 * @param {String} options.compareBy - Compare by object with value and label properties, or legacy string
 * @param {String} options.displayAs - Display format
 * @param {Boolean} options.groupBySegment - If true, creates slides grouped by segment
 * @param {Object} options.pptx
 * @param {Object} options.questionData - Response data for MaxDiff question
 * @param {String} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createMaxDiffChartSlide = ({
  compareBy,
  displayAs,
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  // Always create charts for all segments, regardless of compareBy value
  const maxSlides = Math.min(questionData.segments.length, 10);

  for (let i = 0; i < maxSlides; i++) {
    const segment = questionData.segments[i];

    const { chartData, tableData } = getMaxDiffDisplayData(questionData, displayAs, segment.id);
    if (!chartData) {
      continue;
    }

    const pptChartData = convertMaxDiffChartToPowerPoint(chartData, displayAs, tableData);
    if (!pptChartData) {
      continue;
    }

    const totalOptions = pptChartData.categoryLabels?.length || 0;
    if (totalOptions > 16) {
      createSplitMaxDiffChartSlides({
        chartData: pptChartData,
        compareBy,
        displayAs,
        pptx,
        questionNumber,
        questionTitle,
        reportType,
        reportUrl,
        segmentName: segment.name,
        title,
      });
    } else {
      const reversedCategories = [...pptChartData.categoryLabels].reverse();
      const reversedSeries = pptChartData.dataSeries.map((series) => ({
        ...series,
        labels: reversedCategories,
        values: [...series.values].reverse(),
      }));

      const reversedChartData = {
        ...pptChartData,
        categoryLabels: reversedCategories,
        dataSeries: reversedSeries,
      };

      createSingleMaxDiffSlide({
        chartData: reversedChartData,
        compareBy,
        displayAs,
        pptx,
        questionNumber,
        questionTitle,
        reportType,
        reportUrl,
        segmentName: segment.name,
        title,
      });
    }
  }
};

/**
 * Creates multiple MaxDiff chart slides when there are >20 options
 */
const createSplitMaxDiffChartSlides = ({
  chartData,
  compareBy,
  displayAs,
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  segmentName,
  title,
}) => {
  const totalOptions = chartData.categoryLabels.length;

  // Calculate optimal splits to create more evenly sized slides with consistent spacing
  const { itemsPerSlide: optionsPerSlide, totalSlides } = calculateOptimalSlideDistribution(
    totalOptions,
    12, // maxOptionsPerSlide
    8 // minOptionsPerSlide
  );

  for (let slideIndex = 0; slideIndex < totalSlides; slideIndex++) {
    const startIndex = slideIndex * optionsPerSlide;
    const endIndex = Math.min(startIndex + optionsPerSlide, totalOptions);

    const slideCategories = chartData.categoryLabels.slice(startIndex, endIndex);
    const slideSeries = chartData.dataSeries.map((series) => ({
      ...series,
      labels: slideCategories,
      values: series.values.slice(startIndex, endIndex),
    }));

    const reversedSlideCategories = [...slideCategories].reverse();
    const reversedSlideSeries = slideSeries.map((series) => ({
      ...series,
      labels: reversedSlideCategories,
      values: [...series.values].reverse(),
    }));

    const slideChartData = {
      ...chartData,
      categoryLabels: reversedSlideCategories,
      dataSeries: reversedSlideSeries,
    };

    // Build the slide title with proper segmentation handling
    const cleanTitle = cleanQuestionTitle(questionTitle);
    const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
    const baseTitle = `${questionPrefix}${cleanTitle}`;

    const compareByLabel = compareBy?.label || compareBy?.value || compareBy;
    const isOverall = compareByLabel?.toLowerCase() === 'overall';

    let slideTitle;
    const pageInfo = totalSlides > 1 ? ` - Chart (${slideIndex + 1} of ${totalSlides})` : '';

    if (segmentName && !isOverall) {
      if (compareByLabel) {
        slideTitle = `${baseTitle} Compared by: ${compareByLabel} - ${segmentName}${pageInfo}`;
      } else {
        slideTitle = `${baseTitle} - ${segmentName}${pageInfo}`;
      }
    } else if (compareByLabel) {
      slideTitle = `${baseTitle} Compared by: ${compareByLabel}${pageInfo}`;
    } else {
      slideTitle = `${baseTitle}${pageInfo}`;
    }

    createSingleMaxDiffSlide({
      chartData: slideChartData,
      compareBy,
      displayAs,
      pptx,
      questionNumber,
      questionTitle: slideTitle,
      reportType,
      reportUrl,
      segmentName,
      title,
    });
  }
};

/**
 * Creates a single MaxDiff chart slide
 */
const createSingleMaxDiffSlide = ({
  chartData,
  compareBy,
  displayAs,
  pptx,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  segmentName,
  title,
}) => {
  const slide = pptx.addSlide();

  // Use the questionTitle directly if it's already formatted (from split slides)
  // Otherwise build the title from components
  let slideTitle;
  if (questionTitle.includes('(') && questionTitle.includes('of')) {
    // Already formatted title from split slides
    slideTitle = questionTitle;
  } else {
    // Build title from components
    const cleanTitle = cleanQuestionTitle(questionTitle);
    if (!cleanTitle) {
      return null;
    }

    const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
    const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;

    const compareByLabel = compareBy?.label || compareBy?.value || compareBy;
    const isOverall = compareByLabel?.toLowerCase() === 'overall';

    if (segmentName && !isOverall) {
      if (compareByLabel) {
        slideTitle = `${titleWithQuestionNumber} Compared by: ${compareByLabel} - ${segmentName}`;
      } else {
        slideTitle = `${titleWithQuestionNumber} - ${segmentName}`;
      }
    } else if (compareByLabel) {
      slideTitle = `${titleWithQuestionNumber} Compared by: ${compareByLabel}`;
    } else {
      slideTitle = titleWithQuestionNumber;
    }
  }

  const titleOptions = SURVEY_TITLE_OPTIONS;
  slide.addText(slideTitle, titleOptions);

  const chartYPosition = titleOptions.h > 1.0 ? 1.3 : 1.0;
  const chartWidth = '98%';
  const chartXPosition = 0.05;

  try {
    const cleanedChartData = chartData.dataSeries.map((series) => ({
      ...series,
      values: series.values.map((value) => {
        if (value === null || value === undefined || isNaN(value)) {
          return 0;
        }
        // Ensure finite values for PowerPoint compatibility
        return isFinite(value) ? value : 0;
      }),
    }));

    const isCountDisplay = displayAs === 'count';
    const isPercentageDisplay = chartData.isPercentageDisplay || !isCountDisplay;
    const axisFormatCode = isPercentageDisplay ? '0%' : '#,##0';

    // Standardize chart dimensions for consistent appearance across questions
    const numCategories = cleanedChartData[0]?.labels?.length || 0;
    // Use consistent sizing based on number of categories
    const dynamicChartHeight =
      numCategories <= 8
        ? '65%'
        : numCategories <= 12
          ? '68%'
          : numCategories <= 16
            ? '70%'
            : '72%';
    const dynamicFontSize = numCategories <= 10 ? 9 : numCategories <= 15 ? 8 : 7;

    const chartOptions = {
      barDir: 'bar',
      barGrouping: isPercentageDisplay ? 'percentStacked' : 'stacked',
      catAxisLabelColor: TEXT_COLOR,
      catAxisLabelFontSize: dynamicFontSize,
      catAxisLabelPos: 'low',
      catAxisLabelWrap: true,
      catAxisLineShow: false,
      catAxisMajorGridlines: false,
      chartColors: cleanedChartData.map((series) => series.color),
      h: dynamicChartHeight,
      legendColor: TEXT_COLOR,
      legendFontSize: 10,
      legendPos: 'b',
      showCatAxisTitle: false,
      showLegend: true,
      showTitle: false,
      valAxisLabelColor: TEXT_COLOR,
      valAxisLabelFontSize: 8,
      valAxisLabelFormatCode: axisFormatCode,
      valAxisLineShow: true,
      valAxisMajorGridlines: true,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
      ...(isPercentageDisplay && {
        valAxisLabelFormatCode: '0%',
        valAxisMajorUnit: 0.1,
        valAxisMaxVal: 1.0,
        valAxisMinVal: 0.0,
        valAxisTitle: 'Percentage',
      }),
      ...(!isPercentageDisplay && {
        valAxisLabelFormatCode: '#,##0',
        valAxisMajorUnit: null,
        valAxisMinVal: 0,
        valAxisTitle: 'Count',
      }),
    };

    slide.addChart(pptx.charts.BAR, cleanedChartData, chartOptions);
  } catch (error) {
    slide.addText('MaxDiff chart could not be created', {
      color: TEXT_COLOR,
      fontSize: 12,
      h: 1,
      w: chartWidth,
      x: chartXPosition,
      y: chartYPosition,
    });
  }

  addSlideFooter({ pptx, reportType, reportUrl, slide });
  slide.addText(
    [{ text: 'Source: ' }, { options: { hyperlink: { url: reportUrl } }, text: title }],
    FOOTER_TEXT
  );

  return slide;
};

/**
 * Creates MaxDiff table slides
 * @param {Object} options
 * @param {String} options.compareBy - Compare by object with value and label properties, or legacy string
 * @param {String} options.displayAs - Display format
 * @param {Object} options.pptx
 * @param {Object} options.questionData - Response data for MaxDiff question
 * @param {String} options.questionNumber
 * @param {String} options.questionTitle
 * @param {String} options.reportType
 * @param {String} options.reportUrl
 * @param {String} options.title
 * @returns {Object}
 */
export const createMaxDiffTableSlide = ({
  compareBy,
  displayAs,
  pptx,
  questionData,
  questionNumber,
  questionTitle,
  reportType,
  reportUrl,
  title,
}) => {
  if (!questionTitle || questionTitle.length < 3) {
    return null;
  }

  const cleanTitle = cleanQuestionTitle(questionTitle);
  const questionPrefix = questionNumber ? `Q${questionNumber}. ` : '';
  const titleWithQuestionNumber = `${questionPrefix}${cleanTitle}`;

  // Always create tables for all segments, regardless of compareBy value
  const maxSlides = Math.min(questionData.segments.length, 10);
  for (let i = 0; i < maxSlides; i++) {
    const segment = questionData.segments[i];

    const { tableData } = getMaxDiffDisplayData(questionData, displayAs, segment.id);
    if (!tableData) {
      continue;
    }

    const baseSlideTitle = `${titleWithQuestionNumber} - ${segment.name} - Data Table`;

    // Handle splitting for MaxDiff tables with >16 rows (matching chart logic)
    const totalRows = tableData.data?.length || 0;

    if (totalRows > 16) {
      // Calculate optimal distribution for table rows
      const { itemsPerSlide: rowsPerSlide, totalSlides } = calculateOptimalSlideDistribution(
        totalRows,
        11, // maxRowsPerSlide
        6 // minRowsPerSlide
      );

      let currentIndex = 0;
      for (let slideIndex = 0; slideIndex < totalSlides; slideIndex++) {
        const startIndex = currentIndex;
        const endIndex = Math.min(currentIndex + rowsPerSlide, totalRows);

        const slideTableData = {
          ...tableData,
          data: tableData.data.slice(startIndex, endIndex),
        };

        const slideTitle =
          totalSlides > 1
            ? `${baseSlideTitle} (${slideIndex + 1} of ${totalSlides})`
            : baseSlideTitle;

        createSingleMaxDiffTableSlide({
          pptx,
          reportType,
          reportUrl,
          slideTitle,
          tableData: slideTableData,
          title,
        });

        currentIndex = endIndex;
      }
    } else {
      // Single table slide
      createSingleMaxDiffTableSlide({
        pptx,
        reportType,
        reportUrl,
        slideTitle: baseSlideTitle,
        tableData: tableData,
        title,
      });
    }
  }

  return null;
};
